/**
 * JWT Utilities
 * Helper functions for working with JWT tokens
 */

import { jwtDecode } from 'jwt-decode';
import type { Entitlements } from './storage';

export interface DecodedToken {
  user_id: number;
  username?: string;
  email?: string;
  exp: number;
  iat: number;
  clinic_id?: number;
  clinic_name?: string;
  doctor_id?: number;
  role?: string;
  name?: string;
  /** Practitioner specialty, e.g. 'Rheumatology' | 'GP'. Used by postAuthRedirect. */
  specialty?: string;
    specialties?: string[];
  /**
   * Subscription-entitlement block. Mirrors user_context.entitlements; used as a
   * fallback by the entitlements helper when the stored user lacks the block.
   */
  entitlements?: Entitlements;
}

/**
 * Decode a JWT token and return the payload
 * @param token - The JWT token string
 * @returns The decoded token payload or null if invalid
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    return jwtDecode<DecodedToken>(token);
  } catch (error) {
    console.warn('[jwtUtils] Failed to decode token:', error);
    return null;
  }
}

/**
 * Check if a token is expired
 * @param token - The JWT token string
 * @returns true if expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
}

/**
 * Get the user ID from a token
 * @param token - The JWT token string
 * @returns The user ID or null if not found
 */
export function getUserIdFromToken(token: string): number | null {
  const decoded = decodeToken(token);
  return decoded?.user_id || null;
}

/**
 * Get the clinic ID from a token
 * @param token - The JWT token string
 * @returns The clinic ID or null if not found
 */
export function getClinicIdFromToken(token: string): number | null {
  const decoded = decodeToken(token);
  return decoded?.clinic_id || null;
}

/**
 * Get the doctor ID from a token
 * @param token - The JWT token string
 * @returns The doctor ID or null if not found
 */
export function getDoctorIdFromToken(token: string): number | null {
  const decoded = decodeToken(token);
  return decoded?.doctor_id || null;
}

/**
 * Get the doctor name from a token
 * @param token - The JWT token string
 * @returns The doctor name or null if not found
 */
export function getDoctorNameFromToken(token: string): string | null {
  const decoded = decodeToken(token);
  return decoded?.name || decoded?.username || null;
}

/**
 * Get the time until token expiration in seconds
 * @param token - The JWT token string
 * @returns Seconds until expiration, or 0 if already expired
 */
export function getTokenTimeToExpiry(token: string): number {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Date.now() / 1000;
    const timeToExpiry = decoded.exp - currentTime;
    return Math.max(0, timeToExpiry);
  } catch {
    return 0;
  }
}

const jwtUtils = {
  decodeToken,
  isTokenExpired,
  getUserIdFromToken,
  getClinicIdFromToken,
  getDoctorIdFromToken,
  getDoctorNameFromToken,
  getTokenTimeToExpiry,
};

export default jwtUtils;
